<template>
  <div class="flex flex-col gap-4">
    <div>{{ t('menu-availability.stock.restocking.title') }}</div>

    <transition
      enter-active-class="transition-opacity duration-300 ease-in-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200 ease-in-out"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
      mode="out-in"
    >
      <div
        v-if="!editRestockingMode"
        key="view"
        class="flex justify-between bg-n-800 p-4 rounded-lg"
      >
        <div class="flex flex-col gap-2">
          <div class="flex flex-col">
            <div class="font-bold text-sm">
              {{ t(`menu-availability.stock.refillPolicy.${policy}.title`) }}
            </div>
            <div class="text-xs">
              {{ t(`menu-availability.stock.refillPolicy.${policy}.description`) }}
            </div>
          </div>
          <l-field
            v-if="optionsWithRefillAmount.includes(policy)"
            :label="t('menu-availability.stock.restocking.amount-label')"
          >
            <l-number-input v-model="internalValue" type="number" :min="0" :max="maxValue" />
          </l-field>
        </div>
        <l-button
          type="text"
          size="small"
          icon="edit"
          color="white"
          class="bg-n-600 shadow-[0_3px_6px_0_#00000033]"
          @click="editRestockingMode = true"
        ></l-button>
      </div>

      <div v-else key="edit" class="grid grid-cols-1 gap-2">
        <div v-for="option in options" :key="option">
          <l-card-radio
            name="refillPolicy"
            :value="option"
            :model-value="policy"
            @click="updateRestockingMode(option)"
          >
            <div class="font-body text-sm text-n-0 flex flex-col gap-2">
              <div class="font-bold">
                {{ t(`menu-availability.stock.refillPolicy.${option}.title`) }}
              </div>
              <div class="text-xs">
                {{ t(`menu-availability.stock.refillPolicy.${option}.description`) }}
              </div>
            </div>
          </l-card-radio>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { LButton, LCardRadio, LField, LNumberInput } from '@last/core-ui/paprika'

type RefillOption = 'refill-daily' | 'refill-shift' | 'just-today' | 'until-sold-out'

const { t } = useI18n()

const props = defineProps<{
  policy: RefillOption
  value: number | undefined
  options: Readonly<RefillOption[]>
  optionsWithRefillAmount: Readonly<RefillOption[]>
  maxValue: number
}>()

const emit = defineEmits<{
  'update:policy': [policy: RefillOption]
  'update:value': [value: number | undefined]
}>()

const editRestockingMode = ref(false)

const internalValue = computed({
  get: () => props.value,
  set: val => emit('update:value', val)
})

function updateRestockingMode(option: RefillOption) {
  emit('update:policy', option)
  editRestockingMode.value = false
}
</script>
